import { useMemo, useRef, useState, useEffect } from "react";
import styles from "./leadGate.module.css";

const ZAPIER_BUDGET_WEBHOOK = "https://hooks.zapier.com/hooks/catch/17916584/uhdiqkg/";

/* --- UPDATED OPTIONS --- */
const REASONS = ["First home buyer", "Investment property", "Just browsing"];
const TIMEFRAMES = ["Now", "0–3 months", "3–6 months"];

// Helper: serialize flat object to query string
function serializeToQS(obj) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) p.append(k, String(v));
  }
  return p.toString();
}

export default function LeadGate({
  repaymentAmount = 0,
  onSuccess = () => {},
  onCancel = () => {},
}) {
  const formRef = useRef(null);

  // steps
  const [step, setStep] = useState(1);

  // step 1 (micro-intent)
  const [reason, setReason] = useState("");
  const [timeframe, setTimeframe] = useState("");

  // step 2 (contact)
  const [state, setState] = useState({ loading: false, error: "", success: false });

  // meta
  const [pageUrl, setPageUrl] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setPageUrl(window.location.href);
    }
  }, []);

  const canContinueStep1 = Boolean(reason) && Boolean(timeframe);

  const handleContinue = async (e) => {
    e?.preventDefault?.();

    // STEP 1 -> STEP 2
    if (step === 1) {
      if (!canContinueStep1) return;
      setStep(2);
      return;
    }

    // STEP 2 — submit to Zapier with robust fallbacks
    setState({ loading: true, error: "", success: false });

    // Pull values from the form
    const fdForm = new FormData(formRef.current);
    const full_name = (fdForm.get("full_name") || "").toString();
    const email = (fdForm.get("email") || "").toString();
    const phone = (fdForm.get("phone") || "").toString();
    const honeypot = (fdForm.get("_gotcha") || "").toString();

    // Small idempotency key to help dedupe
    const event_id = `${Date.now()}-${(email || phone || Math.random()).toString().slice(-6)}`;

    // Flat payload (TOP-LEVEL KEYS)
    const payload = {
      source: "Repayments › Check My Budget Gate",
      full_name,
      email,
      phone_mobile: phone,
      reason,
      timeframe,
      repayment_amount_monthly: Math.round(Number(repaymentAmount || 0)),
      page_url: pageUrl,
      submitted_at: new Date().toISOString(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      honeypot,
      event_id,
    };

    async function sendWithFallbacks() {
      const qs = serializeToQS(payload);

      // 1) Normal POST (best for parsing & logs)
      try {
        const r1 = await fetch(ZAPIER_BUDGET_WEBHOOK, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
          body: qs,
          keepalive: true,
          signal: typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(4000) : undefined,
        });
        if (r1.ok) return true;
        console.warn("Zap POST non-2xx:", r1.status);
      } catch (e) {
        console.warn("Zap POST error:", e);
      }

      // 2) no-cors POST (fire-and-forget)
      try {
        await fetch(ZAPIER_BUDGET_WEBHOOK, {
          method: "POST",
          body: qs,           // no headers -> simple request
          mode: "no-cors",
          keepalive: true,
        });
        return true;
      } catch (e) {
        console.warn("Zap POST no-cors error:", e);
      }

      // 3) Pixel GET fallback
      try {
        const img = new Image();
        img.referrerPolicy = "no-referrer-when-downgrade";
        img.src = `${ZAPIER_BUDGET_WEBHOOK}?${qs}`;
        return true; // treat as sent
      } catch (e) {
        console.warn("Zap GET pixel error:", e);
      }

      return false;
    }

    try {
      await sendWithFallbacks();
      setState({ loading: false, error: "", success: true });
      onSuccess(); // continue UX (open calculator, etc.)
    } catch {
      // even if all fail, don't block the user
      setState({ loading: false, error: "", success: true });
      onSuccess();
    }
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Enter your details">
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>
              {step === 1 ? "Let’s tailor your budget check" : "Before we show your budget"}
            </h2>
            <div className={styles.subtitle}>
              {step === 1
                ? "Two quick questions, then we'll open the calculator."
                : "Enter your details and we’ll show your budget."}
            </div>
          </div>
          <button onClick={onCancel} className={styles.close} aria-label="Close">×</button>
        </header>

        {/* STEP 1 — micro-intent */}
        {step === 1 && (
          <section className={styles.body}>
            <div className={styles.sectionLabel}>Why are you checking your budget?</div>
            <div className={styles.optionsGrid}>
              {REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={reason === r ? `${styles.option} ${styles.optionActive}` : styles.option}
                  onClick={() => setReason(r)}
                >
                  {r}
                </button>
              ))}
            </div>

            <div className={styles.sectionLabel} style={{ marginTop: 12 }}>
              When are you planning to move ahead?
            </div>
            <div className={styles.optionsGrid}>
              {TIMEFRAMES.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={timeframe === t ? `${styles.option} ${styles.optionActive}` : styles.option}
                  onClick={() => setTimeframe(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.btnGhost} onClick={onCancel}>Cancel</button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleContinue}
                disabled={!canContinueStep1}
              >
                Continue
              </button>
            </div>
          </section>
        )}

        {/* STEP 2 — contact form */}
        {step === 2 && (
          <form ref={formRef} onSubmit={handleContinue} className={styles.form}>
            <div className={styles.row}>
              <label className={styles.label} htmlFor="full_name">Full name</label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                required
                placeholder="e.g. Alex Smith"
                className={styles.input}
                autoComplete="name"
              />
            </div>

            <div className={styles.rowGrid2}>
              <div>
                <label className={styles.label} htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@email.com"
                  className={styles.input}
                  autoComplete="email"
                  inputMode="email"
                />
              </div>
              <div>
                <label className={styles.label} htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  pattern="^[0-9 +()-]{8,}$"
                  placeholder="04xx xxx xxx"
                  className={styles.input}
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className={styles.helpText}>
              By continuing, you agree we may contact you about your home loan options. We respect your privacy.
            </div>

            {state.error && <div className={styles.error}>{state.error}</div>}

            <div className={styles.actions}>
              <button type="button" className={styles.btnGhost} onClick={() => setStep(1)}>Back</button>
              <button type="submit" className={styles.btnPrimary} disabled={state.loading}>
                {state.loading ? "Submitting..." : "Open Budget Calculator"}
              </button>
            </div>

            {/* Honeypot for bots */}
            <input type="text" name="_gotcha" className={styles.hp} tabIndex="-1" autoComplete="off" />
          </form>
        )}

        <footer className={styles.footerNote}>
          Est. monthly repayment: <strong>${(Number(repaymentAmount || 0)).toLocaleString("en-AU")}</strong>
        </footer>
      </div>
    </div>
  );
}
